import React from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import FunnelProgressBar from 'components/FunnelProgressBar';
import CircularLoader from 'components/CircularLoader';

import icn_bar_chart_horizontal from 'assets/images/icons/icn_bar_chart_horizontal.png';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';

// 自定義 Tooltip 樣式
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 480,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good ≥ 90%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Captive portal connectivity is healthy. Most users authenticate and get online successfully.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning 60–89%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Some users are failing to authenticate or get online. Service quality is starting to degrade.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Review the APs or SSIDs with the lowest success rates.</li>
        <li>Check uplink connectivity, DHCP/DNS behavior, and captive portal configuration.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical {'<'} 60%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        A large number of users cannot get online, indicating a major service disruption or authentication failure.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Verify the captive portal and authentication backend services.</li>
        <li>Isolate affected SSIDs or APs and apply temporary fallback access policies.</li>
      </Box>
    </Box>
  </Box>
);

const CaptivePortalSuccessRate = ({ chartData, isEmpty, onClick, loading }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (val) => {
    if (val >= 90) return { label: 'Good', color: '#4CAF50' };
    if (val >= 60) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const rate = chartData?.attempts > 0 ? Math.round((chartData.success / chartData.attempts) * 100) : 100;
  const status = getStatus(rate);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        pointerEvents: isEmpty ? 'none' : 'auto',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_bar_chart_horizontal}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Captive Portal Connection Success Rate
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start'
        }}>
          <Box sx={{
            display: 'flex',
            width: { xs: '100%', md: '35%' },
            pr: { xs: 0, md: 4 },
            mb: { xs: 3, md: 0 }
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px' }}>Status</Typography>
              <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2 }}>
                {status.label}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px' }}>Success Rate</Typography>
              <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: '#172664', lineHeight: 1.2 }}>
                {rate}%
              </Typography>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' }, mx: 2, borderColor: '#EDF2F7' }}
          />

          <Box sx={{ flex: 1, pl: { xs: 0, md: 2 }, width: '100%' }}>
            <FunnelProgressBar attempts={chartData.attempts} success={chartData.success} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CaptivePortalSuccessRate;
